import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useProperty from 'hooks/Offers/useProperty'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { EmptyObject } from 'lib/object/objectUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import LuxLPPPricingLabel from 'luxPlus/components/LuxLPPPricingLabel'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ExperimentalSearchTileFeaturesList, { MAX_INCLUSIONS_TO_SHOW }
  from 'components/SearchKit/SearchTiles/shared/ExperimentalSearchTileFeaturesList'
import type { SearchTileRowInclusion } from 'components/SearchKit/SearchTiles/shared/SearchTileRowInclusionItem'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import useBedbankRates from 'hooks/useBedbankRates'
import { useActiveAmenityFilters } from 'hooks/useActiveAmenityFilters'
import { reorderAmenitiesByActiveFilters, reorderDescribedAmenitiesByActiveFilters } from 'lib/offer/amenitiesUtils'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import { EmptyArray } from 'lib/array/arrayUtils'
import { mapPropertyPopularFacilitiesToAmenities } from './propertyPopularFacilities'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import SearchTileAmenitiesLoadingSkeleton from '../shared/SearchTileAmenitiesLoadingSkeleton'
import SearchTileFreeCancellationInclusion, {
  type SearchTileCancellationInclusionKind,
  useSearchTileCancellationInclusion,
} from '../shared/SearchTileFreeCancellationInclusion'
import { getCompactInclusionListLimit } from './accommodationSearchTileView'
import {
  resolveMappedSearchFilterInclusions,
  shouldShowCompactMappedInclusionsOverflow,
  useSearchFilterCategoryInclusions,
} from '../shared/useSearchFilterCategoryInclusions'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  maxInclusionsToShow?: number
  hideLuxPlusPricingLabel?: boolean
  hideRoomBreakdown?: boolean
  maxAmenitiesToShow?: number
  hideLoyaltyInclusions?: boolean
  mapToSearchFilterCategories?: boolean
  cancellationInclusionKind?: SearchTileCancellationInclusionKind
}

function ExperimentalBedbankSearchTileFeatures({
  offer,
  filters = EmptyObject,
  maxInclusionsToShow,
  hideLuxPlusPricingLabel = false,
  hideRoomBreakdown = false,
  maxAmenitiesToShow,
  hideLoyaltyInclusions = false,
  mapToSearchFilterCategories = false,
  cancellationInclusionKind,
}: Props) {
  const intl = useIntl()
  const {
    offerPrice,
    offerMemberPrice,
  } = useBedbankOfferSearchPricing(offer, filters)
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const [propertyContent, isFetchingPropertyContent] = useProperty(offerMetaData?.accommodationId)
  const lowestPriceRoomRateId = offerMetaData?.pricing?.lowestPriceRoomRateId
  const hasInclusionFilters = !!filters.inclusions?.length
  const shouldFetchRates = !!lowestPriceRoomRateId && !!filters.checkIn && !!filters.checkOut && hasInclusionFilters
  const [rates, fetchingRates] = useBedbankRates(
    offer.id,
    filters.rooms ?? [{ adults: 2 }],
    filters.checkIn,
    filters.checkOut,
    shouldFetchRates,
  )

  const rateInclusions = useMemo<Array<App.OfferInclusion> | undefined>(() => {
    if (!lowestPriceRoomRateId || !hasInclusionFilters) {
      return undefined
    }

    const allRates = [...rates.hotelOnlyRates, ...rates.hotelBundleRates]
    const rate = allRates.find(r => r.id === lowestPriceRoomRateId)

    // Return undefined if rate not found (will fall back to popularFacilities)
    // Return empty array if rate found but has no inclusions
    return rate?.inclusions
  }, [lowestPriceRoomRateId, rates, hasInclusionFilters])

  const activeAmenityFilters = useActiveAmenityFilters()
  const isUsingRateInclusions = hasInclusionFilters && !!rateInclusions

  const propertyContentAmenities = useMemo<Array<SearchTileRowInclusion>>(() => {
    if (!propertyContent?.popularFacilities.length) return EmptyArray
    return mapPropertyPopularFacilitiesToAmenities(propertyContent.popularFacilities)
  }, [propertyContent?.popularFacilities])

  const cancellationInclusion = useSearchTileCancellationInclusion(cancellationInclusionKind)
  const mappedInclusionLimit = maxInclusionsToShow ?? MAX_INCLUSIONS_TO_SHOW
  const sourceRateInclusions = isUsingRateInclusions ? (rateInclusions ?? EmptyArray) : EmptyArray
  const mappedSearchFilterInclusions = useSearchFilterCategoryInclusions(
    sourceRateInclusions,
    mappedInclusionLimit,
  )
  const isFetchingPropertyAmenities = !isUsingRateInclusions &&
    !!offerMetaData?.accommodationId &&
    isFetchingPropertyContent &&
    !propertyContent
  const displayedRateInclusions = useMemo(() => {
    const mapped = resolveMappedSearchFilterInclusions(
      mapToSearchFilterCategories && isUsingRateInclusions,
      mappedSearchFilterInclusions.inclusions,
      sourceRateInclusions,
    )
    if (!mapToSearchFilterCategories || !isUsingRateInclusions || !cancellationInclusion) return mapped
    return [cancellationInclusion, ...mapped]
  }, [cancellationInclusion, isUsingRateInclusions, mapToSearchFilterCategories, mappedSearchFilterInclusions.inclusions, sourceRateInclusions])
  const inclusionOverflowLabel = shouldShowCompactMappedInclusionsOverflow(
    mapToSearchFilterCategories && isUsingRateInclusions,
    sourceRateInclusions.length,
    mappedSearchFilterInclusions.inclusions.length,
  ) ? mappedSearchFilterInclusions.showMoreLabel : undefined

  const amenities = useMemo<Array<SearchTileRowInclusion>>(() => {
    // Only use rate inclusions if we have inclusion filters selected
    // Otherwise fall back to popularFacilities
    if (isUsingRateInclusions) {
      return EmptyArray
    }

    if (propertyContentAmenities.length > 0) {
      if (activeAmenityFilters.size > 0) {
        return reorderDescribedAmenitiesByActiveFilters(propertyContentAmenities, activeAmenityFilters)
      }

      return propertyContentAmenities
    }

    // Reorder popularFacilities before mapping to boost matching amenities
    const reorderedFacilities = activeAmenityFilters.size > 0
      ? reorderAmenitiesByActiveFilters(offer.popularFacilities, activeAmenityFilters)
      : offer.popularFacilities

    return reorderedFacilities.map(facility => ({
      description: facility,
      id: facility,
      symbol: determineOfferFeatureSymbol(facility),
    }))
  }, [activeAmenityFilters, isUsingRateInclusions, offer.popularFacilities, propertyContentAmenities])

  const luxPlusLPPEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const directSearchPrices = useDirectSearchPrices({ filters, offerId: offer.id })
  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionTo={getHotelOfferUrl(offer.id, offer.type)}
      primaryActionText={intl.formatMessage({
        id: 'wetNSQuFlvaIbNR2MKlW',
        defaultMessage: 'View offer',
      })}
    >
      <Group direction="vertical" gap={4}>
        <OfferInclusionsList inclusions={isUsingRateInclusions ? sourceRateInclusions : amenities} />
        {!mapToSearchFilterCategories && !hideLoyaltyInclusions && <LoyaltyInclusions offer={offer} variant="offer-page" />}
      </Group>
    </Modal>)
  }, [amenities, hideLoyaltyInclusions, intl, isUsingRateInclusions, mapToSearchFilterCategories, offer, showModal, sourceRateInclusions])

  const savingAmount = offerMetaData?.pricing?.lowestMemberPrice && offerMetaData?.pricing?.lowestPrice ?
    offerMetaData?.pricing?.lowestPrice - offerMetaData?.pricing?.lowestMemberPrice :
    undefined

  const isFetchingSearchTileFeatures = (shouldFetchRates && fetchingRates) || isFetchingPropertyAmenities
  const maxFeaturesToShow = isUsingRateInclusions ? maxInclusionsToShow : maxAmenitiesToShow ?? maxInclusionsToShow
  const amenitiesLimit = getCompactInclusionListLimit(maxFeaturesToShow ?? MAX_INCLUSIONS_TO_SHOW, false)
  const mappedListLimit = getCompactInclusionListLimit(
    mappedInclusionLimit,
    mapToSearchFilterCategories && !!cancellationInclusion,
  )
  const amenitiesInclusionSize = mapToSearchFilterCategories ? 'small' as const : undefined
  const amenitiesSingleColumn = !!rateInclusions || mapToSearchFilterCategories

  const featuresContent = isFetchingSearchTileFeatures ? (
    <SearchTileAmenitiesLoadingSkeleton />
  ) : (
    <>
      {!mapToSearchFilterCategories && <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />}
      <Group direction="vertical" gap={4}>
        {isUsingRateInclusions && (!!displayedRateInclusions.length || !!inclusionOverflowLabel) && <ExperimentalSearchTileFeaturesList
          features={displayedRateInclusions}
          featuresType="inclusions"
          onMoreClick={openAllInclusionsModal}
          maxInclusionsToShow={mappedListLimit}
          overflowLabel={inclusionOverflowLabel}
          inclusionSize={mapToSearchFilterCategories ? 'small' : undefined}
        />}
        {!isUsingRateInclusions && <ExperimentalSearchTileFeaturesList
          features={amenities}
          featuresType="amenities"
          leading={cancellationInclusionKind ? (
            <SearchTileFreeCancellationInclusion kind={cancellationInclusionKind} />
          ) : undefined}
          onMoreClick={openAllInclusionsModal}
          singleColumn={amenitiesSingleColumn}
          maxInclusionsToShow={amenitiesLimit}
          inclusionSize={amenitiesInclusionSize}
        />}
        {!mapToSearchFilterCategories && !hideLoyaltyInclusions && <LoyaltyInclusions offer={offer} variant="search-tile" />}
      </Group>
      {luxPlusLPPEnabled && !hideLuxPlusPricingLabel && !!offerMetaData?.pricing?.lowestMemberPrice && !!savingAmount && savingAmount > 0 && <LuxLPPPricingLabel
        offer={offer}
        hasLuxPlusRate={directSearchPrices?.hasLuxPlusRate}
        offerMemberPrice={offerMemberPrice}
        offerPrice={offerPrice}
        savingAmount={savingAmount}
      />}
    </>
  )

  return <Group direction="vertical" verticalAlign="space-between" grow gap={8} noDisplayWhenEmpty>
    <Group direction="vertical" gap={8} noDisplayWhenEmpty>
      {featuresContent}
    </Group>
    {!hideRoomBreakdown && <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
      isCroCompact={mapToSearchFilterCategories}
    />}
  </Group>
}

export default ExperimentalBedbankSearchTileFeatures
